import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth';
import { PortalAuthProvider, usePortalAuth } from '@/lib/portalAuth';
import { BrandingProvider } from '@/lib/branding';
import { QueryProvider } from '@/lib/queryProvider';
import { Toaster } from '@/components/ui/sonner';
import { AppDialogProvider } from '@/components/ui/AppDialogProvider';
import { AppShell } from '@/components/layout/AppShell';
import { PortalShell } from '@/components/layout/PortalShell';
import { ForceChangePasswordModal } from '@/components/shared/ForceChangePassword';

// Pages
import LoginPage from '@/pages/LoginPage';
import Dashboard from '@/pages/Dashboard';
import StaffManagement from '@/pages/admin/StaffManagement';
import RolesPage from '@/pages/admin/RolesPage';
import OrgSettingsPage from '@/pages/admin/OrgSettingsPage';
import StaffListPage from '@/pages/staff/StaffListPage';
import StaffDetailPage from '@/pages/staff/StaffDetailPage';
import StaffAvailabilityViewerPage from '@/pages/workforce/StaffAvailabilityViewerPage';
import ParticipantListPage from '@/pages/participants/ParticipantListPage';
import ParticipantDetailPage from '@/pages/participants/ParticipantDetailPage';
import RosterAdmin from '@/pages/admin/RosterAdmin';
import TimesheetApproval from '@/pages/admin/TimesheetApproval';
import TimesheetApprovalV2Page from '@/pages/timesheets/TimesheetApprovalV2Page';
import Invoices from '@/pages/admin/Invoices';
import PortalRosterPage from '@/pages/portal/PortalRosterPage';
import ShiftDetail from '@/pages/staff/ShiftDetail';
import MyTimesheetsV2Page from '@/pages/timesheets/MyTimesheetsV2Page';
import ServiceCatalog from '@/pages/admin/ServiceCatalog';
import AuditLogPage from '@/pages/admin/AuditLogPage';
import NotificationsPage from '@/pages/notifications/NotificationsPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import IncidentListPage from '@/pages/incidents/IncidentListPage';
import IncidentCreatePage from '@/pages/incidents/IncidentCreatePage';
import IncidentDetailPage from '@/pages/incidents/IncidentDetailPage';
import IncidentIntelligencePage from '@/pages/incidents/IncidentIntelligencePage';
import ClaimListPage from '@/pages/finance/ClaimListPage';
import ClaimDetailPage from '@/pages/finance/ClaimDetailPage';
import ComplianceDashboardPage from '@/pages/compliance/ComplianceDashboardPage';
import RestrictivePracticesPage from '@/pages/compliance/RestrictivePracticesPage';
import LeaveApprovalsPage from '@/pages/hr/LeaveApprovalsPage';
import PortalLeavePage from '@/pages/portal/PortalLeavePage';
import PortalAvailabilityPage from '@/pages/portal/PortalAvailabilityPage';
import MedicationDashboardPage from '@/pages/clinical/MedicationDashboardPage';
import RecruitmentPipelinePage from '@/pages/hr/RecruitmentPipelinePage';
import CandidateProfilePage from '@/pages/hr/CandidateProfilePage';
import HRDashboardPage from '@/pages/hr/HRDashboardPage';
import OnboardingPage from '@/pages/hr/OnboardingPage';
import HousesListPage from '@/pages/accommodation/HousesListPage';
import HouseRoomsPage from '@/pages/accommodation/HouseRoomsPage';
import HouseDashboardPage from '@/pages/houses/HouseDashboardPage';
import MaintenanceListPage from '@/pages/maintenance/MaintenanceListPage';
import PayrollPeriodsPage from '@/pages/finance/payroll/PayrollPeriodsPage';
import PayrollBatchesPage from '@/pages/finance/payroll/PayrollBatchesPage';
import PayrollBatchDetailPage from '@/pages/finance/payroll/PayrollBatchDetailPage';
import PayrollExceptionsPage from '@/pages/finance/payroll/PayrollExceptionsPage';
import PayrollAdminPage from '@/pages/admin/PayrollAdminPage';
import MissedClaimsPage from '@/pages/finance/MissedClaimsPage';
import FinancePipelinePage from '@/pages/finance/FinancePipelinePage';
import WorkforceIntelligencePage from '@/pages/workforce/WorkforceIntelligencePage';
import SILIntelligencePage from '@/pages/accommodation/SILIntelligencePage';
import StaffCostsReportPage from '@/pages/reports/StaffCostsReportPage';

// Day-11: Dashboards
import ExecutiveDashboardPage from '@/pages/dashboards/ExecutiveDashboardPage';
import OperationsDashboardPage from '@/pages/dashboards/OperationsDashboardPage';
import FinanceDashboardPage from '@/pages/dashboards/FinanceDashboardPage';
import HRDashboardPageNew from '@/pages/dashboards/HRDashboardPage';
import ClinicalDashboardPage from '@/pages/dashboards/ClinicalDashboardPage';
import AlertsPage from '@/pages/dashboards/AlertsPage';
import ActionDashboard from '@/pages/dashboards/ActionDashboard';
import SavedReportsPage from '@/pages/reports/SavedReportsPage';
import ReportBuilderPage from '@/pages/reports/ReportBuilderPage';

// Day-12: Portal Pages
import PortalLoginPage from '@/pages/portal/PortalLoginPage';
import PortalForgotPasswordPage from '@/pages/portal/PortalForgotPasswordPage';
import PortalDashboardPage from '@/pages/portal/PortalDashboardPage';
import PortalShiftsPage from '@/pages/portal/PortalShiftsPage';
import PortalCarePlanPage from '@/pages/portal/PortalCarePlanPage';
import PortalMedicationsPage from '@/pages/portal/PortalMedicationsPage';
import PortalInvoicesPage from '@/pages/portal/PortalInvoicesPage';
import PortalMessagesPage from '@/pages/portal/PortalMessagesPage';
import PortalReferralPage from '@/pages/portal/PortalReferralPage';
import ReferralDashboardPage from '@/pages/intake/ReferralDashboardPage';

// Day-13: Document Management
import DocumentLibraryPage from '@/pages/documents/DocumentLibraryPage';
import DocumentTemplatesPage from '@/pages/documents/DocumentTemplatesPage';
import ESignRequestsPage from '@/pages/documents/ESignRequestsPage';
import DocumentExportPage from '@/pages/documents/DocumentExportPage';
import StaffPoliciesPage from '@/pages/staff/StaffPoliciesPage';
import ParticipantDocumentRequestsPage from '@/pages/participants/ParticipantDocumentRequestsPage';
import ParticipantChartDetailPage from '@/pages/participants/charts/ParticipantChartDetailPage';
import ReviewOutcomesExplorerPage from '@/pages/participants/charts/ReviewOutcomesExplorerPage';
import PortalDocumentsPage from '@/pages/portal/PortalDocumentsPage';

// Day-14: AI Intelligence Layer
import FundingForecastPage from '@/pages/insights/FundingForecastPage';
import RiskDashboardPage from '@/pages/insights/RiskDashboardPage';
import RecommendationsPage from '@/pages/insights/RecommendationsPage';
import AICopilotPage from '@/pages/ai/AICopilotPage';
import AIIntelligencePage from '@/pages/ai/AIIntelligencePage';
import SavedInsightsPage from '@/pages/insights/SavedInsightsPage';

// Staff Dashboard
import StaffDashboard from '@/pages/staff/StaffDashboard';
import MyParticipantsPage from '@/pages/staff/MyParticipantsPage';

// Day-15: Mobile Field Operations
import MobileStaffDashboardPage from '@/pages/mobile/MobileStaffDashboardPage';
import MobileProgressNotePage from '@/pages/mobile/MobileProgressNotePage';
import MobileIncidentPage from '@/pages/mobile/MobileIncidentPage';
import MobileHandoverPage from '@/pages/mobile/MobileHandoverPage';
import MobilePendingSyncPage from '@/pages/mobile/MobilePendingSyncPage';
import MobileMedicationPage from '@/pages/mobile/MobileMedicationPage';
import MobileAttachmentUploadPage from '@/pages/mobile/MobileAttachmentUploadPage';
import MobileNotificationsPage from '@/pages/mobile/MobileNotificationsPage';

// Day-16: Communications & Integrations UI
import CommunicationTemplatesPage from '@/pages/settings/CommunicationTemplatesPage';
import { SMSLogPage, EmailLogPage } from '@/pages/communications/CommunicationLogPages';
import CommunicationHubPage from '@/pages/communications/CommunicationHubPage';
import AccountingIntegrationPage from '@/pages/settings/AccountingIntegrationPage';
import PublicFormManagementPage from '@/pages/settings/PublicFormManagementPage';
import ClaimExportPage from '@/pages/finance/ClaimExportPage';
import WebhookManagementPage from '@/pages/settings/WebhookManagementPage';

// Section 9: Document + Onboarding Intelligence
import DocumentIntelligencePage from '@/pages/compliance/DocumentIntelligencePage';

// Day 12: Exception Queue
import ExceptionQueuePage from '@/pages/admin/ExceptionQueuePage';

// Day 17: Corrections
import CorrectionsPage from '@/pages/admin/CorrectionsPage';

// Day 18: Invoice Regeneration
import InvoiceRegenerationPage from '@/pages/finance/InvoiceRegenerationPage';

// Admin Feature Flags UI
import FeatureFlagsPage from '@/pages/admin/FeatureFlagsPage';
import ParityAuditPage from '@/pages/admin/ParityAuditPage';
import LiveSplitBillingGovernancePage from '@/pages/admin/LiveSplitBillingGovernancePage';
import ActivationCommandCenterPage from '@/pages/admin/ActivationCommandCenterPage';

// Ratio Engine
import RatioRulesPage from '@/pages/admin/RatioRulesPage';
import SleepoverSettingsPage from '@/pages/admin/SleepoverSettingsPage';
import SleepoverEstimateComparisonPage from '@/pages/admin/SleepoverEstimateComparisonPage';

// Release, Reports, Digest
import ReleaseSignoffPage from '@/pages/admin/ReleaseSignoffPage';
import ReportsPage from '@/pages/admin/ReportsPage';
import DigestPage from '@/pages/admin/DigestPage';
import RolloutDashboardPage from '@/pages/admin/RolloutDashboardPage';
// Day 15: Smart Alerts
import SmartAlertsPage from '@/pages/admin/SmartAlertsPage';
import SmartAlertDetailPage from '@/pages/admin/SmartAlertDetailPage';
// Day 17: Predictive Staffing
import PredictiveStaffingPage from '@/pages/admin/PredictiveStaffingPage';
// Day 18: Revenue Optimization
import RevenueOptimizationPage from '@/pages/admin/RevenueOptimizationPage';
// Day 19: Compliance & Audit
import ComplianceAuditPage from '@/pages/admin/ComplianceDashboardPage';
// Day 20: Audit Reporting
import AuditReportingPage from '@/pages/admin/AuditReportingPage';
// Day 24: Soft Launch
import SoftLaunchPage from '@/pages/admin/SoftLaunchPage';
// Day 25: Client Onboarding
import OnboardingDashboardPage from '@/pages/admin/OnboardingDashboardPage';
import OnboardingWizardPage from '@/pages/admin/OnboardingWizardPage';
import OnboardingDetailPage from '@/pages/admin/OnboardingDetailPage';
import OnboardingImportPage from '@/pages/admin/OnboardingImportPage';
import OnboardingReviewPage from '@/pages/admin/OnboardingReviewPage';
// Day 2: Group Sessions
import GroupSessionsPage from '@/pages/admin/GroupSessionsPage';
import GroupSessionDetailPage from '@/pages/admin/GroupSessionDetailPage';

// Day-17: Roster Intelligence
import RosterInsightsPage from '@/pages/roster/RosterInsightsPage';
import BulkAssignShiftsPage from '@/pages/roster/BulkAssignShiftsPage';

// Day-17b: Advanced Rostering
import RecurringSeriesPage from '@/pages/roster/RecurringSeriesPage';
import ShiftTemplatesPage from '@/pages/roster/ShiftTemplatesPage';
import WorkforceDashboardPage from '@/pages/roster/WorkforceDashboardPage';
import ShiftSwapRequestsPage from '@/pages/roster/ShiftSwapRequestsPage';
import SILRosterPage from '@/pages/roster/SILRosterPage';
import CentreSessionsPage from '@/pages/admin/CentreSessionsPage';
import TeamRosterPage from '@/pages/roster/TeamRosterPage';
import RosterCalendarPage from '@/pages/roster/RosterCalendarPage';
import CalendarV3 from '@/pages/calendar/CalendarV3';
import AssetManagementPage from '@/pages/assets/AssetManagementPage';
import StaffAvailabilityPage from '@/pages/roster/StaffAvailabilityPage';
import MyShiftsPage from '@/pages/roster/MyShiftsPage';

// Teams
import TeamsListPage from '@/pages/teams/TeamsListPage';
import TeamFormPage from '@/pages/teams/TeamFormPage';
import TeamDetailPage from '@/pages/teams/TeamDetailPage';

// Accommodation Extended
import AccommodationDashboardPage from '@/pages/accommodation/AccommodationDashboardPage';
import PlacementsPage from '@/pages/accommodation/PlacementsPage';
import VisitorLogsPage from '@/pages/accommodation/VisitorLogsPage';

// Day-18: Engagement
import AppointmentsPage from '@/pages/engagement/AppointmentsPage';
import CommunicationCentrePage from '@/pages/engagement/CommunicationCentrePage';
import FeedbackPage, { PortalFeedbackPage } from '@/pages/engagement/FeedbackPage';
import AnnouncementsPage from '@/pages/engagement/AnnouncementsPage';

// Day-19: Security
import SecurityDashboardPage from '@/pages/admin/SecurityDashboardPage';
import SecurityAlertsPage from '@/pages/admin/SecurityAlertsPage';
// Day-20: System Admin
import SystemAdminPage from '@/pages/admin/SystemAdminPage';
// Branding
import BrandingSettingsPage from '@/pages/admin/BrandingSettingsPage';
// Onboarding Portal
import OnboardingPortalPage from '@/pages/onboarding/OnboardingPortalPage';
import SetupPasswordPage from '@/pages/auth/SetupPasswordPage';
import DocumentReviewDashboardPage from '@/pages/hr/DocumentReviewDashboardPage';

// Form Builder
import FormsDashboardPage from '@/pages/forms/FormsDashboardPage';
import FormTemplatesPage from '@/pages/forms/FormTemplatesPage';
import FormTemplateBuilderPage from '@/pages/forms/FormTemplateBuilderPage';
import FormTemplateDetailPage from '@/pages/forms/FormTemplateDetailPage';
import FormAssignmentsPage from '@/pages/forms/FormAssignmentsPage';
import FormFillPage from '@/pages/forms/FormFillPage';
import FormPrintPage from '@/pages/forms/FormPrintPage';

function ProtectedRoute({ children, requireAdmin = false, requireSuperAdmin = false, allowStaff = false }) {
  const { user, loading, isAdmin, mustChangePassword, onPasswordChanged } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-background"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  
  // Check if user is a staff/support worker (non-admin)
  const isStaffRole = user?.role_name === 'Support Worker';
  
  // Redirect staff away from admin areas
  if (requireAdmin && !isAdmin) return <Navigate to={isStaffRole ? "/staff/dashboard" : "/dashboard"} replace />;
  if (requireSuperAdmin && user?.role_name !== 'Super Admin') return <Navigate to={isStaffRole ? "/staff/dashboard" : "/dashboard"} replace />;
  
  return (
    <AppShell>
      {mustChangePassword && (
        <ForceChangePasswordModal open={true} onSuccess={onPasswordChanged} />
      )}
      {children}
    </AppShell>
  );
}

function AuthRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-background"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (user) {
    // Redirect staff to their dashboard, admins to executive dashboard
    const isStaffRole = user?.role_name === 'Support Worker';
    return <Navigate to={isStaffRole ? "/staff/dashboard" : "/dashboard"} replace />;
  }
  return children;
}

function PortalAuthRoute({ children }) {
  const { user, loading } = usePortalAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-background"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (user) return <Navigate to="/portal/dashboard" replace />;
  return children;
}

function PortalProtectedRoute({ children }) {
  const { user, loading } = usePortalAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-background"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/portal/login" replace />;
  return <PortalShell>{children}</PortalShell>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
      <Route path="/onboarding/:token" element={<OnboardingPortalPage />} />
      <Route path="/setup-password" element={<SetupPasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Day-12: External Portal Routes (separate auth) */}
      <Route path="/portal/login" element={<PortalAuthRoute><PortalLoginPage /></PortalAuthRoute>} />
      <Route path="/portal/forgot-password" element={<PortalForgotPasswordPage />} />
      <Route path="/portal/dashboard" element={<PortalProtectedRoute><PortalDashboardPage /></PortalProtectedRoute>} />
      <Route path="/portal/shifts" element={<PortalProtectedRoute><PortalShiftsPage /></PortalProtectedRoute>} />
      <Route path="/portal/care-plan" element={<PortalProtectedRoute><PortalCarePlanPage /></PortalProtectedRoute>} />
      <Route path="/portal/medications" element={<PortalProtectedRoute><PortalMedicationsPage /></PortalProtectedRoute>} />
      <Route path="/portal/invoices" element={<PortalProtectedRoute><PortalInvoicesPage /></PortalProtectedRoute>} />
      <Route path="/portal/messages" element={<PortalProtectedRoute><PortalMessagesPage /></PortalProtectedRoute>} />
      <Route path="/portal/referral" element={<PortalProtectedRoute><PortalReferralPage /></PortalProtectedRoute>} />
      <Route path="/portal/documents" element={<PortalProtectedRoute><PortalDocumentsPage /></PortalProtectedRoute>} />
      <Route path="/portal/feedback" element={<PortalProtectedRoute><PortalFeedbackPage /></PortalProtectedRoute>} />

      {/* Admin */}
      <Route path="/admin/users" element={<ProtectedRoute requireAdmin><StaffManagement /></ProtectedRoute>} />
      <Route path="/admin/roles" element={<ProtectedRoute requireAdmin><RolesPage /></ProtectedRoute>} />
      <Route path="/admin/audit" element={<ProtectedRoute requireAdmin><AuditLogPage /></ProtectedRoute>} />

      {/* 7.4 Settings */}
      <Route path="/org/settings" element={<ProtectedRoute requireAdmin><OrgSettingsPage /></ProtectedRoute>} />

      {/* 7.5 Staff */}
      <Route path="/staff/list" element={<ProtectedRoute requireAdmin><StaffListPage /></ProtectedRoute>} />
      <Route path="/staff/:staffId" element={<ProtectedRoute requireAdmin><StaffDetailPage /></ProtectedRoute>} />
      <Route path="/workforce/availability" element={<ProtectedRoute requireAdmin><StaffAvailabilityViewerPage /></ProtectedRoute>} />

      {/* 7.6 Participants */}
      <Route path="/participants/list" element={<ProtectedRoute><ParticipantListPage /></ProtectedRoute>} />
      <Route path="/participants/:participantId" element={<ProtectedRoute><ParticipantDetailPage /></ProtectedRoute>} />

      {/* 7.7 Rostering */}
      <Route path="/roster/calendar" element={<ProtectedRoute requireAdmin><RosterAdmin /></ProtectedRoute>} />

      {/* Day-17b: Advanced Rostering */}
      <Route path="/roster/recurring" element={<ProtectedRoute requireAdmin><RecurringSeriesPage /></ProtectedRoute>} />
      <Route path="/roster/templates" element={<ProtectedRoute requireAdmin><ShiftTemplatesPage /></ProtectedRoute>} />
      <Route path="/roster/workforce" element={<ProtectedRoute requireAdmin><WorkforceDashboardPage /></ProtectedRoute>} />
      <Route path="/roster/swap-requests" element={<ProtectedRoute requireAdmin><ShiftSwapRequestsPage /></ProtectedRoute>} />
      <Route path="/roster/sil" element={<ProtectedRoute requireAdmin><SILRosterPage /></ProtectedRoute>} />
      <Route path="/admin/centre-sessions" element={<ProtectedRoute requireAdmin><CentreSessionsPage /></ProtectedRoute>} />
      <Route path="/roster/teams" element={<ProtectedRoute requireAdmin><TeamRosterPage /></ProtectedRoute>} />
      <Route path="/roster/visual" element={<ProtectedRoute requireAdmin><RosterCalendarPage /></ProtectedRoute>} />
      <Route path="/roster/scheduling-board" element={<ProtectedRoute requireAdmin><CalendarV3 /></ProtectedRoute>} />
      <Route path="/asset-management" element={<ProtectedRoute requireAdmin><AssetManagementPage /></ProtectedRoute>} />
      <Route path="/roster/staff-availability" element={<ProtectedRoute requireAdmin><StaffAvailabilityPage /></ProtectedRoute>} />
      <Route path="/roster/my-shifts" element={<ProtectedRoute><MyShiftsPage /></ProtectedRoute>} />

      {/* Services (admin) */}
      <Route path="/admin/services" element={<ProtectedRoute requireAdmin><ServiceCatalog /></ProtectedRoute>} />

      {/* 7.8 Staff Portal */}
      <Route path="/portal/roster" element={<ProtectedRoute><PortalRosterPage /></ProtectedRoute>} />
      <Route path="/portal/shift/:shiftId" element={<ProtectedRoute><ShiftDetail /></ProtectedRoute>} />
      <Route path="/portal/leave" element={<ProtectedRoute><PortalLeavePage /></ProtectedRoute>} />
      <Route path="/portal/availability" element={<ProtectedRoute><PortalAvailabilityPage /></ProtectedRoute>} />

      {/* 7.9 Timesheets */}
      <Route path="/timesheets/pending" element={<ProtectedRoute requireAdmin><TimesheetApproval /></ProtectedRoute>} />
      <Route path="/timesheets/approval" element={<ProtectedRoute requireAdmin><TimesheetApprovalV2Page /></ProtectedRoute>} />
      <Route path="/timesheets/my-v2" element={<ProtectedRoute><MyTimesheetsV2Page /></ProtectedRoute>} />

      {/* Day 12: Exception Queue */}
      <Route path="/admin/attendance-exceptions" element={<ProtectedRoute requireAdmin><ExceptionQueuePage /></ProtectedRoute>} />

      {/* Day 17: Corrections */}
      <Route path="/admin/corrections" element={<ProtectedRoute requireAdmin><CorrectionsPage /></ProtectedRoute>} />

      {/* 7.10 Finance */}
      <Route path="/finance/invoices" element={<ProtectedRoute requireAdmin><Invoices /></ProtectedRoute>} />

      {/* Day 18: Invoice Regeneration */}
      <Route path="/finance/invoice-regeneration" element={<ProtectedRoute requireAdmin><InvoiceRegenerationPage /></ProtectedRoute>} />

      {/* Notifications */}
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

      {/* Compliance / Incidents */}
      <Route path="/compliance/incidents" element={<ProtectedRoute><IncidentListPage /></ProtectedRoute>} />
      <Route path="/compliance/incidents/new" element={<ProtectedRoute><IncidentCreatePage /></ProtectedRoute>} />
      <Route path="/compliance/incidents/intelligence" element={<ProtectedRoute><IncidentIntelligencePage /></ProtectedRoute>} />
      <Route path="/compliance/incidents/:incidentId" element={<ProtectedRoute><IncidentDetailPage /></ProtectedRoute>} />
      <Route path="/compliance/staff" element={<ProtectedRoute requireAdmin><ComplianceDashboardPage /></ProtectedRoute>} />
      <Route path="/compliance/restrictive-practices" element={<ProtectedRoute><RestrictivePracticesPage /></ProtectedRoute>} />
      <Route path="/compliance/document-intelligence" element={<ProtectedRoute><DocumentIntelligencePage /></ProtectedRoute>} />
      <Route path="/hr/leave" element={<ProtectedRoute requireAdmin><LeaveApprovalsPage /></ProtectedRoute>} />
      <Route path="/hr/dashboard" element={<ProtectedRoute requireAdmin><HRDashboardPage /></ProtectedRoute>} />
      <Route path="/hr/recruitment" element={<ProtectedRoute requireAdmin><RecruitmentPipelinePage /></ProtectedRoute>} />
      <Route path="/hr/candidates/:candidateId" element={<ProtectedRoute requireAdmin><CandidateProfilePage /></ProtectedRoute>} />
      <Route path="/hr/onboarding/:staffId" element={<ProtectedRoute requireAdmin><OnboardingPage /></ProtectedRoute>} />
      <Route path="/clinical/medications" element={<ProtectedRoute requireAdmin><MedicationDashboardPage /></ProtectedRoute>} />

      {/* Finance: Claims */}
      <Route path="/finance/claims" element={<ProtectedRoute requireAdmin><ClaimListPage /></ProtectedRoute>} />
      <Route path="/finance/claims/:claimId" element={<ProtectedRoute requireAdmin><ClaimDetailPage /></ProtectedRoute>} />
      <Route path="/finance/missed-claims" element={<ProtectedRoute requireAdmin><MissedClaimsPage /></ProtectedRoute>} />
      <Route path="/finance/pipeline" element={<ProtectedRoute><FinancePipelinePage /></ProtectedRoute>} />
      <Route path="/workforce/intelligence" element={<ProtectedRoute><WorkforceIntelligencePage /></ProtectedRoute>} />
      <Route path="/accommodation/sil-intelligence" element={<ProtectedRoute requireAdmin><SILIntelligencePage /></ProtectedRoute>} />

      {/* Day-9: Accommodation/SIL */}
      <Route path="/accommodation/houses" element={<ProtectedRoute><HousesListPage /></ProtectedRoute>} />
      <Route path="/accommodation/houses/:houseId/rooms" element={<ProtectedRoute><HouseRoomsPage /></ProtectedRoute>} />
      <Route path="/houses/:houseId" element={<ProtectedRoute><HouseDashboardPage /></ProtectedRoute>} />
      <Route path="/maintenance" element={<ProtectedRoute><MaintenanceListPage /></ProtectedRoute>} />

      {/* Day-10: Payroll */}
      <Route path="/finance/payroll-periods" element={<ProtectedRoute requireAdmin><PayrollPeriodsPage /></ProtectedRoute>} />
      <Route path="/finance/payroll" element={<ProtectedRoute requireAdmin><PayrollBatchesPage /></ProtectedRoute>} />
      <Route path="/finance/payroll/:batchId" element={<ProtectedRoute requireAdmin><PayrollBatchDetailPage /></ProtectedRoute>} />
      <Route path="/finance/payroll/:batchId/exceptions" element={<ProtectedRoute requireAdmin><PayrollExceptionsPage /></ProtectedRoute>} />
      <Route path="/reports/staff-costs" element={<ProtectedRoute requireAdmin><StaffCostsReportPage /></ProtectedRoute>} />
      <Route path="/admin/payroll" element={<ProtectedRoute requireAdmin><PayrollAdminPage /></ProtectedRoute>} />

      {/* Day-11: Management Dashboards (New Routes) - ADMIN ONLY */}
      <Route path="/dashboard/operations" element={<ProtectedRoute requireAdmin><OperationsDashboardPage /></ProtectedRoute>} />
      <Route path="/dashboard/finance" element={<ProtectedRoute requireAdmin><FinanceDashboardPage /></ProtectedRoute>} />
      <Route path="/dashboard/hr" element={<ProtectedRoute requireAdmin><HRDashboardPageNew /></ProtectedRoute>} />
      <Route path="/dashboard/clinical" element={<ProtectedRoute requireAdmin><ClinicalDashboardPage /></ProtectedRoute>} />
      <Route path="/dashboard/actions" element={<ProtectedRoute requireAdmin><ActionDashboard /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute requireAdmin><ExecutiveDashboardPage /></ProtectedRoute>} />
      
      {/* Staff-specific dashboard */}
      <Route path="/staff/dashboard" element={<ProtectedRoute><StaffDashboard /></ProtectedRoute>} />
      <Route path="/staff/my-participants" element={<ProtectedRoute><MyParticipantsPage /></ProtectedRoute>} />
      
      <Route path="/alerts" element={<ProtectedRoute requireAdmin><AlertsPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute requireAdmin><ReportBuilderPage /></ProtectedRoute>} />
      
      {/* Legacy dashboard routes - redirect to new paths */}
      {/* Day-30 cleanup: legacy /dashboards/* redirects removed.
          Canonical paths in active use: /dashboard, /dashboard/operations,
          /dashboard/clinical, /dashboard/finance, /dashboard/hr, /alerts. */}

      {/* Day-12: Intake/Referrals (Internal) */}
      <Route path="/intake/referrals" element={<ProtectedRoute><ReferralDashboardPage /></ProtectedRoute>} />

      {/* Day-13: Document Management */}
      <Route path="/documents" element={<ProtectedRoute><DocumentLibraryPage /></ProtectedRoute>} />
      <Route path="/documents/templates" element={<ProtectedRoute><DocumentTemplatesPage /></ProtectedRoute>} />
      <Route path="/documents/esign" element={<ProtectedRoute><ESignRequestsPage /></ProtectedRoute>} />
      <Route path="/documents/export" element={<ProtectedRoute><DocumentExportPage /></ProtectedRoute>} />
      <Route path="/staff/policies" element={<ProtectedRoute><StaffPoliciesPage /></ProtectedRoute>} />
      <Route path="/participants/:participantId/document-requests" element={<ProtectedRoute><ParticipantDocumentRequestsPage /></ProtectedRoute>} />
      <Route path="/participants/:participantId/charts/:chartId" element={<ProtectedRoute><ParticipantChartDetailPage /></ProtectedRoute>} />
      <Route path="/charts/review-outcomes" element={<ProtectedRoute><ReviewOutcomesExplorerPage /></ProtectedRoute>} />

      {/* Day-14: AI Intelligence Layer */}
      <Route path="/insights/funding" element={<ProtectedRoute><FundingForecastPage /></ProtectedRoute>} />
      <Route path="/insights/risk" element={<ProtectedRoute><RiskDashboardPage /></ProtectedRoute>} />
      <Route path="/insights/recommendations" element={<ProtectedRoute><RecommendationsPage /></ProtectedRoute>} />
      <Route path="/insights/saved" element={<ProtectedRoute><SavedInsightsPage /></ProtectedRoute>} />
      <Route path="/ai" element={<ProtectedRoute><AICopilotPage /></ProtectedRoute>} />
      <Route path="/ai/intelligence" element={<ProtectedRoute><AIIntelligencePage /></ProtectedRoute>} />

      {/* Day-15: Mobile Field Operations */}
      <Route path="/mobile" element={<ProtectedRoute><MobileStaffDashboardPage /></ProtectedRoute>} />
      <Route path="/mobile/notes/new" element={<ProtectedRoute><MobileProgressNotePage /></ProtectedRoute>} />
      <Route path="/mobile/incidents/new" element={<ProtectedRoute><MobileIncidentPage /></ProtectedRoute>} />
      <Route path="/mobile/handover/new" element={<ProtectedRoute><MobileHandoverPage /></ProtectedRoute>} />
      <Route path="/mobile/medications/log" element={<ProtectedRoute><MobileMedicationPage /></ProtectedRoute>} />
      <Route path="/mobile/attachments" element={<ProtectedRoute><MobileAttachmentUploadPage /></ProtectedRoute>} />
      <Route path="/mobile/notifications" element={<ProtectedRoute><MobileNotificationsPage /></ProtectedRoute>} />
      <Route path="/mobile/sync" element={<ProtectedRoute><MobilePendingSyncPage /></ProtectedRoute>} />

      {/* Day-16: Communications & Integrations */}
      <Route path="/settings/communications/templates" element={<ProtectedRoute><CommunicationTemplatesPage /></ProtectedRoute>} />
      <Route path="/communications/hub" element={<ProtectedRoute><CommunicationHubPage /></ProtectedRoute>} />
      <Route path="/communications/sms" element={<ProtectedRoute><SMSLogPage /></ProtectedRoute>} />
      <Route path="/communications/email" element={<ProtectedRoute><EmailLogPage /></ProtectedRoute>} />
      <Route path="/settings/integrations/accounting" element={<ProtectedRoute><AccountingIntegrationPage /></ProtectedRoute>} />
      <Route path="/settings/public-forms" element={<ProtectedRoute><PublicFormManagementPage /></ProtectedRoute>} />
      <Route path="/finance/claims/:id/export" element={<ProtectedRoute><ClaimExportPage /></ProtectedRoute>} />
      <Route path="/settings/integrations/webhooks" element={<ProtectedRoute><WebhookManagementPage /></ProtectedRoute>} />
      <Route path="/roster/insights" element={<ProtectedRoute><RosterInsightsPage /></ProtectedRoute>} />
      <Route path="/roster/bulk-assign" element={<ProtectedRoute><BulkAssignShiftsPage /></ProtectedRoute>} />

      {/* Teams */}
      <Route path="/teams" element={<ProtectedRoute requireAdmin><TeamsListPage /></ProtectedRoute>} />
      <Route path="/teams/new" element={<ProtectedRoute requireAdmin><TeamFormPage /></ProtectedRoute>} />
      <Route path="/teams/:teamId" element={<ProtectedRoute requireAdmin><TeamDetailPage /></ProtectedRoute>} />
      <Route path="/teams/:teamId/edit" element={<ProtectedRoute requireAdmin><TeamFormPage /></ProtectedRoute>} />

      {/* Accommodation Extended */}
      <Route path="/accommodation/dashboard" element={<ProtectedRoute><AccommodationDashboardPage /></ProtectedRoute>} />
      <Route path="/accommodation/placements" element={<ProtectedRoute requireAdmin><PlacementsPage /></ProtectedRoute>} />
      <Route path="/accommodation/visitors" element={<ProtectedRoute><VisitorLogsPage /></ProtectedRoute>} />
      <Route path="/engagement/appointments" element={<ProtectedRoute><AppointmentsPage /></ProtectedRoute>} />
      <Route path="/engagement/messages" element={<ProtectedRoute><CommunicationCentrePage /></ProtectedRoute>} />
      <Route path="/engagement/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
      <Route path="/engagement/announcements" element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />
      <Route path="/admin/security" element={<ProtectedRoute requireAdmin><SecurityDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/security/alerts" element={<ProtectedRoute requireAdmin><SecurityAlertsPage /></ProtectedRoute>} />
      <Route path="/admin/system" element={<ProtectedRoute requireAdmin><SystemAdminPage /></ProtectedRoute>} />
      <Route path="/admin/settings/branding" element={<ProtectedRoute requireSuperAdmin><BrandingSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/settings/feature-flags" element={<ProtectedRoute requireAdmin><FeatureFlagsPage /></ProtectedRoute>} />
      <Route path="/admin/allocations/parity-audit" element={<ProtectedRoute requireAdmin><ParityAuditPage /></ProtectedRoute>} />
      <Route path="/admin/allocations/live-governance" element={<ProtectedRoute requireAdmin><LiveSplitBillingGovernancePage /></ProtectedRoute>} />
      <Route path="/admin/allocations/command-center" element={<ProtectedRoute requireAdmin><ActivationCommandCenterPage /></ProtectedRoute>} />
      <Route path="/admin/settings/ratio-rules" element={<ProtectedRoute requireAdmin><RatioRulesPage /></ProtectedRoute>} />
      <Route path="/admin/settings/sleepover" element={<ProtectedRoute requireAdmin><SleepoverSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/sleepover/estimate-comparison" element={<ProtectedRoute requireAdmin><SleepoverEstimateComparisonPage /></ProtectedRoute>} />
      <Route path="/admin/group-sessions" element={<ProtectedRoute requireAdmin><GroupSessionsPage /></ProtectedRoute>} />
      <Route path="/admin/group-sessions/:sessionId" element={<ProtectedRoute requireAdmin><GroupSessionDetailPage /></ProtectedRoute>} />
      <Route path="/admin/release-signoff" element={<ProtectedRoute requireAdmin><ReleaseSignoffPage /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute requireAdmin><ReportsPage /></ProtectedRoute>} />
      <Route path="/admin/digest" element={<ProtectedRoute requireAdmin><DigestPage /></ProtectedRoute>} />
      <Route path="/admin/rollout" element={<ProtectedRoute requireAdmin><RolloutDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/smart-alerts" element={<ProtectedRoute requireAdmin><SmartAlertsPage /></ProtectedRoute>} />
      <Route path="/admin/smart-alerts/:id" element={<ProtectedRoute requireAdmin><SmartAlertDetailPage /></ProtectedRoute>} />
      <Route path="/admin/predictive-staffing" element={<ProtectedRoute requireAdmin><PredictiveStaffingPage /></ProtectedRoute>} />
      <Route path="/admin/revenue" element={<ProtectedRoute requireAdmin><RevenueOptimizationPage /></ProtectedRoute>} />
      <Route path="/admin/compliance" element={<ProtectedRoute requireAdmin><ComplianceAuditPage /></ProtectedRoute>} />
      <Route path="/admin/audit-reporting" element={<ProtectedRoute requireAdmin><AuditReportingPage /></ProtectedRoute>} />
      <Route path="/admin/soft-launch" element={<ProtectedRoute requireAdmin><SoftLaunchPage /></ProtectedRoute>} />
      {/* Day 25: Client Onboarding */}
      <Route path="/admin/onboarding" element={<ProtectedRoute requireSuperAdmin><OnboardingDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/onboarding/new" element={<ProtectedRoute requireSuperAdmin><OnboardingWizardPage /></ProtectedRoute>} />
      <Route path="/admin/onboarding/:id" element={<ProtectedRoute requireSuperAdmin><OnboardingDetailPage /></ProtectedRoute>} />
      <Route path="/admin/onboarding/:id/import" element={<ProtectedRoute requireSuperAdmin><OnboardingImportPage /></ProtectedRoute>} />
      <Route path="/admin/onboarding/:id/review" element={<ProtectedRoute requireAdmin><OnboardingReviewPage /></ProtectedRoute>} />
      <Route path="/hr/document-review" element={<ProtectedRoute><DocumentReviewDashboardPage /></ProtectedRoute>} />

      {/* Form Builder */}
      <Route path="/forms/dashboard" element={<ProtectedRoute><FormsDashboardPage /></ProtectedRoute>} />
      <Route path="/forms/templates" element={<ProtectedRoute><FormTemplatesPage /></ProtectedRoute>} />
      <Route path="/forms/templates/new" element={<ProtectedRoute><FormTemplateBuilderPage /></ProtectedRoute>} />
      <Route path="/forms/templates/:templateId" element={<ProtectedRoute><FormTemplateDetailPage /></ProtectedRoute>} />
      <Route path="/forms/templates/:templateId/edit" element={<ProtectedRoute><FormTemplateBuilderPage /></ProtectedRoute>} />
      <Route path="/forms/assignments" element={<ProtectedRoute><FormAssignmentsPage /></ProtectedRoute>} />
      <Route path="/forms/assignments/:assignmentId/fill" element={<ProtectedRoute><FormFillPage /></ProtectedRoute>} />
      <Route path="/forms/assignments/:assignmentId/print" element={<ProtectedRoute><FormPrintPage /></ProtectedRoute>} />

      {/* Redirects */}
      <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
      <Route path="/staff" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <QueryProvider>
        <AuthProvider>
          <BrandingProvider>
          <PortalAuthProvider>
            <AppDialogProvider>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </AppDialogProvider>
          </PortalAuthProvider>
          </BrandingProvider>
        </AuthProvider>
      </QueryProvider>
    </BrowserRouter>
  );
}

export default App;
